import React from "react";
import styled from "@emotion/styled";

import { MedicationCardHeader } from "./MedicationCardHeader";

export type FulfillmentStep = {
  heading: string;
  body: string;
  completed?: boolean;
};

export type FulfillmentHelpLink = { url: string; label: string };

type Props = {
  title: string;
  medicationName: string;
  medicationImageUrl: string | null;
  steps: FulfillmentStep[];
  helpLink: FulfillmentHelpLink | null;
  helpLinkFallbackText: string;
  showHelpRow?: boolean;
  primaryLabel: string;
  onPrimary: () => void | Promise<void>;
  primaryDisabled?: boolean;
  submitting?: boolean;
  onDone: () => void;
};

const Page = styled.div`
  min-height: 100vh;
  background: white;
  display: flex;
  flex-direction: column;
  max-width: 375px;
  margin: 0 auto;
  font-family: "Untitled Sans", sans-serif;
`;

const MedCard = styled(MedicationCardHeader)`
  margin: 8px 16px 0;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #191717;
  margin: 20px 16px 0;
  line-height: 1.2;
`;

const StepsContainer = styled.div`
  margin: 24px 16px 0;
  flex: 1;
`;

const StepRow = styled.div`
  display: flex;
  gap: 14px;
  position: relative;
`;

const StepIndicatorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

const StepCircle = styled.div<{ completed?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ completed }) => (completed ? "#1d3a44" : "#6cc1b6")};
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
`;

const ConnectingLine = styled.div`
  width: 2px;
  flex: 1;
  background: #6cc1b6;
  margin: 2px 0;
`;

const StepContent = styled.div`
  padding-bottom: 24px;
`;

const StepHeading = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: #191717;
  margin: 0 0 6px;
  line-height: 1.3;
`;

const StepBody = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #191717;
  margin: 0;
  line-height: 1.5;
`;

const UrlRow = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #191717;
  margin: 12px 0 0;
  line-height: 1.5;
`;

const PharmacyLink = styled.a`
  color: #05727a;
  text-decoration: underline;
`;

const ButtonsArea = styled.div`
  padding: 16px 16px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  height: 56px;
  background: ${({ disabled }) => (disabled ? "#9bbabd" : "#05727a")};
  color: white;
  border: none;
  border-radius: 100px;
  font-size: 16px;
  font-weight: 600;
  font-family: "Untitled Sans", sans-serif;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: ${({ disabled }) => (disabled ? 0.85 : 1)};
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease;
`;

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const DoneButton = styled.button`
  width: 100%;
  height: 56px;
  background: white;
  color: #191717;
  border: 1.5px solid #191717;
  border-radius: 100px;
  font-size: 16px;
  font-weight: 500;
  font-family: "Untitled Sans", sans-serif;
  cursor: pointer;
`;

const CheckmarkIcon = () => (
  <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
    <path
      d="M1 4L4.5 7.5L11 1"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FulfillmentPageShell: React.FC<Props> = ({
  title,
  medicationName,
  medicationImageUrl,
  steps,
  helpLink,
  helpLinkFallbackText,
  showHelpRow = true,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  submitting = false,
  onDone,
}) => (
  <Page>
    <MedCard
      name={medicationName}
      imageUrl={medicationImageUrl}
      imageAlt={medicationName}
      variant="prominent"
      imageOverflow
    />

    <Title>{title}</Title>

    <StepsContainer>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        return (
          <StepRow key={index}>
            <StepIndicatorWrapper>
              <StepCircle completed={step.completed}>
                {step.completed ? <CheckmarkIcon /> : index + 1}
              </StepCircle>
              {!isLast && <ConnectingLine />}
            </StepIndicatorWrapper>
            <StepContent>
              <StepHeading>{step.heading}</StepHeading>
              <StepBody>{step.body}</StepBody>
              {isLast && showHelpRow && (helpLink || helpLinkFallbackText) && (
                <UrlRow>
                  For more details or questions, visit{" "}
                  {helpLink ? (
                    <PharmacyLink
                      href={helpLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {helpLink.label}
                    </PharmacyLink>
                  ) : (
                    <>{helpLinkFallbackText}</>
                  )}
                  .
                </UrlRow>
              )}
            </StepContent>
          </StepRow>
        );
      })}
    </StepsContainer>

    <ButtonsArea>
      <PrimaryButton
        onClick={onPrimary}
        disabled={primaryDisabled}
        aria-busy={submitting}
        aria-label={submitting ? `${primaryLabel}, submitting` : primaryLabel}
      >
        {submitting ? (
          <>
            <Spinner aria-hidden="true" />
            Submitting…
          </>
        ) : (
          primaryLabel
        )}
      </PrimaryButton>
      <DoneButton onClick={onDone}>Done</DoneButton>
    </ButtonsArea>
  </Page>
);
